import React, { useMemo } from 'react'
import { Map } from 'immutable'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import ImmutablePropTypes from 'react-immutable-proptypes'
import { connect as connectRedux } from 'react-redux'
import _get from 'lodash/get'
import _isBoolean from 'lodash/isBoolean'
import { useMutation, gql } from '@apollo/client'
import Icon, { ICON_TYPES, ICON_STYLES } from '../../Icon.v2/index'
import { TYPE_USER_PLAYLIST_ADD_REMOVE } from '../../../services/dialog/index'
import Button, { TYPES } from '../../Button/index'
import UniqueId from '../../UniqueId/index'
import { getBoundActions } from '../../../actions/index'
import {
  ADDED_TO_PLAY_LIST_GA4_EVENT,
  PLAYLIST_TILE_CTA_CLICKED_GA4_EVENT,
  REMOVED_FROM_PLAY_LIST_GA4_EVENT,
} from '../../../services/event-tracking/index'
import {
  PLAYLIST_TYPE_DEFAULT,
  PLAYLIST_TYPE_FAVORITES,
  getToastyMessage,
  getPlaylistCacheKey,
} from '../../../services/playlist/index'
import { TYPE_CONTENT_SERIES, TYPE_CONTENT_VIDEO } from '../../../services/content-type/index'
import { ADD_TO_PLAYLIST, REMOVE_FROM_PLAYLIST } from '../../../services/playlist/query'
import Plus from '../../Icon/icons/Plus/index'

/**
 * The playlist add/remove button component
 * @param {Object} props - The props
 * @param {Map} props.staticText - The static text
 * @param {number} props.contentId - The content id
 * @param {string} props.contentType - The content type
 * @param {string} props.className - The additional class name
 * @param {string} props.labelClassName - The additional label class name
 * @param {Object} props.ga4EventData - The GA4 event data
 * @param {boolean} props.reverse - The value indicating text and icon order
 * @param {boolean} props.circlularIcon - The value indicating icon type
 * @param {boolean} props.buttonIcon - The value indicating button with icon type
 * @param {boolean} props.notext - The value indicating text appearance
 * @param {string} props.buttonLabel - The button label
 * @returns {JSX.Element} PlaylistAddRemoveGraphql
 */
function PlaylistAddRemoveGraphql (props) {
  const {
    notext,
    authJwt,
    reverse,
    uniqueId,
    addToasty,
    className,
    labelClassName,
    staticText,
    contentId,
    contentType = TYPE_CONTENT_VIDEO,
    contentTitle,
    renderModal,
    ga4EventData,
    circlularIcon,
    setEventDefault,
    buttonSvgIcon,
    tipText,
    buttonLabel,
  } = props

  const buttonText = buttonLabel || staticText.get('addToPlaylist')

  const [
    addToPlaylistMutation,
    { error: addToPlaylistError },
  ] = useMutation(ADD_TO_PLAYLIST, {
    update: (cache, { data }) => {
      cache.modify({
        fields: {
          playlistItemList: (existing, { storeFieldName }) => {
            const playlistItem = _get(data, ['playlistCreatePlaylistItem', 'playlistItem'])
            const playlist = _get(playlistItem, ['playlist'])
            if (playlist) {
              const cacheKey = getPlaylistCacheKey(playlist.id, playlist.playlistType)
              if (storeFieldName === cacheKey) {
                const newItemRef = cache.writeFragment({
                  data: playlistItem,
                  fragment: gql`
                    fragment NewPlaylistItem on PlaylistItem {
                      id
                      playlistId
                      contentType
                      contentId
                      titleGroupId
                      playlistPosition
                    }
                  `,
                })
                return {
                  ...existing,
                  playlistItems: [
                    newItemRef,
                    ...existing.playlistItems,
                  ],
                }
              }
            }
            return existing
          },
        },
      })
    },
    onQueryUpdated: (observableQuery) => {
      return observableQuery.refetch()
    },
    context: {
      jwt: authJwt,
    },
  })
  const [
    removeFromPlaylistMutation,
    { error: removeFromPlaylistError },
  ] = useMutation(REMOVE_FROM_PLAYLIST, {
    update: (cache, { data }) => {
      cache.modify({
        fields: {
          playlistItemList: (existing = {}, { storeFieldName, readField }) => {
            const playlistItem = _get(data, ['playlistDeletePlaylistItem', 'playlistItem'])
            const playlist = _get(playlistItem, ['playlist'])
            const existingItems = _get(existing, ['playlistItems'], [])
            if (playlist) {
              const cacheKey = getPlaylistCacheKey(playlist.id, playlist.playlistType)
              if (storeFieldName === cacheKey) {
                return {
                  ...existing,
                  count: existing.count > 0 ? existing.count - 1 : existing.count,
                  playlistItems: existingItems.filter((itemRef) => readField('id', itemRef) !== playlistItem.id),
                }
              }
            }
            return existing
          },
        },
      })
    },
    onQueryUpdated: (observableQuery) => {
      return observableQuery.refetch()
    },
    context: {
      jwt: authJwt,
    },
  })

  const handleButtonClick = () => {
    renderModal(TYPE_USER_PLAYLIST_ADD_REMOVE, {
      onChange: handleChange,
      onReturnFocus: () => {},
      contentId,
      uniqueId,
      uiLocation: 'Manage Playlist Modal',
      uiElement: ga4EventData.ui_element,
      tipText,
    })

    // fire CTA click event
    setEventDefault(PLAYLIST_TILE_CTA_CLICKED_GA4_EVENT.mergeDeep(ga4EventData))
  }

  const handleChange = async (data) => {
    const {
      playlistId,
      playlistType,
      playlistName,
      action,
      callback,
    } = data
    const ga4Options = [PLAYLIST_TYPE_DEFAULT, PLAYLIST_TYPE_FAVORITES].includes(playlistType)
      ? { playlist_type: playlistName }
      : { playlist_name: playlistName, playlist_type: 'Custom' }

    if (action === 'add') {
      await addToPlaylist({ playlistId, playlistType })

      if (!addToPlaylistError) {
        // GA4 event
        setEventDefault(ADDED_TO_PLAY_LIST_GA4_EVENT
          .mergeDeep(ga4Options)
          .mergeDeep(ga4EventData))
      }
    } else {
      await removeFromPlaylist({ playlistId, playlistType })

      if (!removeFromPlaylistError) {
        // GA4 event
        setEventDefault(REMOVED_FROM_PLAY_LIST_GA4_EVENT
          .mergeDeep(ga4Options)
          .mergeDeep(ga4EventData))
      }
    }

    document.dispatchEvent(new CustomEvent('community:playlist-updated'))

    if (callback) {
      callback()
    }

    addToasty(getToastyMessage({
      staticText,
      action,
      error: action === 'add' ? addToPlaylistError : removeFromPlaylistError,
      contentTitle,
      playlistName,
      playlistType,
    }))
  }

  const addToPlaylist = (options) => {
    const {
      playlistId,
      playlistType,
    } = options
    return addToPlaylistMutation({
      variables: {
        contentId,
        contentType,
        playlistId,
        playlistType,
      },
    })
  }

  const removeFromPlaylist = (options) => {
    const {
      playlistId,
      playlistType,
    } = options
    return removeFromPlaylistMutation({
      variables: {
        contentId,
        contentType,
        playlistId,
        playlistType,
      },
    })
  }

  const divClassName = [
    circlularIcon ? 'playlist-add playlist-add__circular' : 'playlist-add',
    reverse ? 'playlist-add--reverse' : null,
    notext ? 'playlist-add--notext' : null,
    className,
  ].filter((cls) => !!cls).join(' ')

  const label = useMemo(() => {
    const icon = circlularIcon ? null : <Icon style={ICON_STYLES.OUTLINE} type={ICON_TYPES.PLUS} />
    const span = notext ? null : <span className="playlist-add__label">{buttonText}</span>
    return (
      <React.Fragment>
        { reverse ? span : icon }
        { reverse ? icon : span }
      </React.Fragment>
    )
  }, [circlularIcon, notext, reverse, buttonText])

  if (buttonSvgIcon) {
    return (
      <Button
        type={TYPES.ICON}
        iconSvg={_isBoolean(buttonSvgIcon) ? <Plus /> : buttonSvgIcon}
        onClick={handleButtonClick}
        className={className ?? 'playlist-add__cta'}
        aria-label={notext ? buttonText : undefined}
      >
        {
          !notext ? (
            <span className={labelClassName ?? 'playlist-add__cta-label'}>
              {buttonText}
            </span>
          ) : null
        }
      </Button>
    )
  }

  return (
    <div className={divClassName}>
      { circlularIcon
        ? <Button
            className="playlist-add__cta button--with-icon playlist-add__cta--add-remove"
            onClick={handleButtonClick}
            type={TYPES.ICON}
            icon={ICON_TYPES.CIRCULAR_ADD}
            aria-label={notext ? buttonText : undefined}
          >
            {label}
          </Button>
        : <Button
            className="playlist-add__cta"
            onClick={handleButtonClick}
            type={TYPES.GHOST}
            aria-label={notext ? buttonText : undefined}
          >
            {label}
          </Button>
      }
    </div>
  )
}

PlaylistAddRemoveGraphql.defaultProps = {
  staticText: Map(),
}

PlaylistAddRemoveGraphql.propTypes = {
  staticText: ImmutablePropTypes.map.isRequired,
  contentId: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]).isRequired,
  contentType: PropTypes.oneOf([
    TYPE_CONTENT_VIDEO,
    TYPE_CONTENT_SERIES,
  ]),
  contentTitle: PropTypes.string.isRequired,
  upstreamContext: ImmutablePropTypes.map,
  uniqueId: PropTypes.string.isRequired,
  className: PropTypes.string,
  labelClassName: PropTypes.string,
  reverse: PropTypes.bool,
  notext: PropTypes.bool,
  ga4EventData: PropTypes.object,
  circlularIcon: PropTypes.bool,
  buttonSvgIcon: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.node,
  ]),
  tipText: PropTypes.string,
  buttonLabel: PropTypes.string,
}

export default compose(
  UniqueId('playlist-add'),
  connectRedux(
    (state) => ({
      authJwt: state.auth.get('jwt'),
      staticText: state.staticText.getIn(['data', 'playlistAddRemove', 'data'], Map()),
    }),
    (dispatch) => {
      const actions = getBoundActions(dispatch)
      return {
        addToasty: actions.toasty.addToasty,
        dismissModal: actions.dialog.dismissModal,
        renderModal: actions.dialog.renderModal,
        setEventDefault: actions.eventTracking.setEventDefault,
      }
    },
  ),
)(PlaylistAddRemoveGraphql)
